import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { getIsLoggedIn } from '@app/ducks';
import { loadOrdersQuery } from '@app/services/selectionRepository';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Selections } from '@typings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export const INFINITE_SELECTIONS_QUERY_KEY = 'infiniteSelectionsList';

type Props = Pick<Partial<Selections.FilterData>, 'search' | 'buyer' | 'statuses'>;

export const useInfiniteSelectionsList = ({ search = '', statuses = ['open'] }: Props = {}) => {
  const { t } = useTranslation(['selections', 'genericErrors']);

  const isUserLoggedIn = useSelector(getIsLoggedIn);

  const data = {
    limit: DEFAULT_ITEMS_COUNT_PER_REQUEST,
    search,
    statuses,
  };

  return useInfiniteQuery({
    enabled: isUserLoggedIn,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.orderCount > DEFAULT_ITEMS_COUNT_PER_REQUEST * lastPageParam) {
        return lastPageParam + 1;
      }

      return undefined;
    },
    initialPageParam: 1,
    meta: {
      errorDescription: t('genericErrors:refresh_page_error'),
      errorMessage: t('selections:selections_fetch_failed'),
    },
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }: { pageParam: number }) =>
      loadOrdersQuery({ ...data, skipFirst: (pageParam - 1) * DEFAULT_ITEMS_COUNT_PER_REQUEST }),
    queryKey: [INFINITE_SELECTIONS_QUERY_KEY, { data }],
  });
};
